import axios from 'axios';
import { getAuthToken } from './authToken';

// Create axios instance with base URL
const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api',
    withCredentials: true,
});

// Admin write routes now require a Firebase ID token server-side; attach it
// whenever an admin session exists. getAuthToken() is a no-op on public pages
// (see utils/authToken.js) — this file must NOT import config/firebase
// directly, or every public page would pull in the Firebase Auth bundle that
// today only loads under /admin.
api.interceptors.request.use(async (config) => {
    const token = await getAuthToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        console.error('API Error:', error);
        return Promise.reject(error);
    }
);

export const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/uploads/')) {
        const backendBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        return `${backendBase}${url}`;
    }
    return url;
};

export const newsService = {
    getAll: async (params) => {
        const response = await api.get('/news', { params });
        return response.data;
    },
    getById: async (id) => {
        const response = await api.get(`/news/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/news', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/news/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/news/${id}`);
        return response.data;
    },
    uploadImage: async (id, file) => {
        const formData = new FormData();
        formData.append('news_id', id);
        formData.append('file', file);
        const response = await api.post('/news/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const projectsService = {
    getAll: async (params) => {
        const response = await api.get('/projects', { params });
        return response.data;
    },
    getById: async (id) => {
        const response = await api.get(`/projects/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/projects', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/projects/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/projects/${id}`);
        return response.data;
    },
    uploadImages: async (id, files) => {
        const formData = new FormData();
        formData.append('project_id', id);
        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }
        const response = await api.post('/projects/upload-images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
    deleteImage: async (projectId, imageUrl) => {
        const response = await api.post('/projects/delete-image', {
            project_id: projectId,
            image_url: imageUrl
        });
        return response.data;
    },
    // kept for backward compatibility if needed, but uploadImages should be preferred
    uploadImage: async (id, file) => {
        // Just wrap it in array
        const formData = new FormData();
        formData.append('project_id', id);
        formData.append('files', file);
        const response = await api.post('/projects/upload-images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const announcementsService = {
    getAll: async (params) => {
        const response = await api.get('/announcements', { params });
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/announcements', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/announcements/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/announcements/${id}`);
        return response.data;
    },
    uploadDocument: async (id, file) => {
        const formData = new FormData();
        formData.append('announcement_id', id);
        formData.append('file', file);
        const response = await api.post('/announcements/upload-document', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const tendersService = {
    getAll: async (params) => {
        const response = await api.get('/tenders', { params });
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/tenders', data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/tenders/${id}`);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/tenders/${id}`, data);
        return response.data;
    },
    uploadDocument: async (id, file) => {
        const formData = new FormData();
        formData.append('tender_id', id);
        formData.append('file', file);
        const response = await api.post('/tenders/upload-document', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const eventsService = {
    getAll: async (params) => {
        const response = await api.get('/events', { params });
        return response.data;
    },
    getById: async (id) => {
        const response = await api.get(`/events/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/events', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/events/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/events/${id}`);
        return response.data;
    },
    uploadImage: async (id, file) => {
        const formData = new FormData();
        formData.append('event_id', id);
        formData.append('file', file);
        const response = await api.post('/events/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const mayorService = {
    get: async () => {
        const response = await api.get('/mayor-message');
        return response.data;
    },
    update: async (data) => {
        const response = await api.put('/mayor-message', data);
        return response.data;
    },
    uploadImage: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/mayor-message/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const institutionsService = {
    getAll: async (params) => {
        const response = await api.get('/institutions', { params });
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/institutions', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/institutions/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/institutions/${id}`);
        return response.data;
    },
    uploadLogo: async (id, file) => {
        const formData = new FormData();
        formData.append('institution_id', id);
        formData.append('file', file);
        const response = await api.post('/institutions/upload-logo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const departmentService = {
    getAll: async (params) => {
        const response = await api.get('/departments', { params });
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/departments', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/departments/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/departments/${id}`);
        return response.data;
    },
};

export const photoGalleryService = {
    getAll: async () => {
        const response = await api.get('/photo-galleries');
        return response.data;
    },
    getById: async (id) => {
        const response = await api.get(`/photo-galleries/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/photo-galleries', data);
        return response.data;
    },
    uploadPhoto: async (id, file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(`/photo-galleries/${id}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
    getPhotos: async (id) => {
        const response = await api.get(`/photo-galleries/${id}/photos`);
        return response.data;
    },
    deletePhoto: async (galleryId, photoId) => {
        const response = await api.delete(`/photo-galleries/${galleryId}/photos/${photoId}`);
        return response.data;
    },
    delete: async (galleryId) => {
        const response = await api.delete(`/photo-galleries/${galleryId}`);
        return response.data;
    },
};

export const videoService = {
    getAll: async () => {
        const response = await api.get('/videos');
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/videos', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/videos/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/videos/${id}`);
        return response.data;
    },
};

// QuickAccessCards and ServiceGuideSection both call getAll() on mount at the
// same time on the homepage — dedupe identical concurrent requests into one.
let servicesInFlight = null;

export const serviceService = {
    getAll: async (params) => {
        if (!params) {
            if (!servicesInFlight) {
                servicesInFlight = api.get('/services')
                    .then((response) => response.data)
                    .finally(() => { servicesInFlight = null; });
            }
            return servicesInFlight;
        }
        const response = await api.get('/services', { params });
        return response.data;
    },
    getBySlug: async (slug) => {
        const response = await api.get(`/services/${slug}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/services', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/services/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/services/${id}`);
        return response.data;
    },
    uploadImage: async (id, file) => {
        const formData = new FormData();
        formData.append('service_id', id);
        formData.append('file', file);
        const response = await api.post('/services/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const serviceItemService = {
    getAllByCategoryId: async (serviceId, params) => {
        const response = await api.get(`/services/${serviceId}/items`, { params });
        return response.data;
    },
    getById: async (id) => {
        const response = await api.get(`/service-items/${id}`);
        return response.data;
    },
    create: async (serviceId, data) => {
        const response = await api.post(`/services/${serviceId}/items`, data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/service-items/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/service-items/${id}`);
        return response.data;
    },
    uploadFile: async (id, file) => {
        const formData = new FormData();
        formData.append('item_id', id);
        formData.append('file', file);
        const response = await api.post('/service-items/upload-file', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
    deleteFile: async (itemId, fileUrl) => {
        const response = await api.post('/service-items/delete-file', {
            item_id: itemId,
            file_url: fileUrl
        });
        return response.data;
    },
};

export const contactService = {
    create: async (data) => {
        const response = await api.post('/contact', data);
        return response.data;
    },
    getAll: async () => {
        const response = await api.get('/contact');
        return response.data;
    },
    updateStatus: async (id, status) => {
        const formData = new FormData();
        formData.append('status', status);
        const response = await api.patch(`/contact/${id}`, formData);
        return response.data;
    },
};

export const legislationService = {
    getAll: async () => {
        const response = await api.get('/legislation');
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/legislation', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/legislation/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/legislation/${id}`);
        return response.data;
    },
    uploadDocument: async (id, file) => {
        const formData = new FormData();
        formData.append('legislation_id', id);
        formData.append('file', file);
        const response = await api.post('/legislation/upload-document', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const councilMemberService = {
    getAll: async () => {
        const response = await api.get('/council-members');
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/council-members', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/council-members/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/council-members/${id}`);
        return response.data;
    },
    uploadPhoto: async (id, file) => {
        const formData = new FormData();
        formData.append('member_id', id);
        formData.append('file', file);
        const response = await api.post('/council-members/upload-photo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const activityReportService = {
    getAll: async () => {
        const response = await api.get('/activity-reports');
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/activity-reports', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/activity-reports/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/activity-reports/${id}`);
        return response.data;
    },
    uploadDocument: async (id, file) => {
        const formData = new FormData();
        formData.append('report_id', id);
        formData.append('file', file);
        const response = await api.post('/activity-reports/upload-document', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const informationRequestService = {
    create: async (data) => {
        const response = await api.post('/information-requests', data);
        return response.data;
    },
    getAll: async (params) => {
        const response = await api.get('/information-requests', { params });
        return response.data;
    },
    updateStatus: async (id, status) => {
        const response = await api.patch(`/information-requests/${id}`, { status });
        return response.data;
    },
};

export const analyticsService = {
    trackPageview: async (path) => {
        try {
            await api.post('/analytics/pageview', { path });
        } catch (error) {
            // Sessizce yut — sayfa görüntüleme kaydı başarısız olsa da site kullanımını etkilememeli
        }
    },
    getSummary: async () => {
        const response = await api.get('/analytics/summary');
        return response.data;
    },
};

export default api;
